import { useEffect, useState } from "react";
import { X, Star, ArrowUpRight, Trash, Share, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { enableShare } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const drawerImageSrc = (r) => {
  if (r.asset_id) return `${API}/assets/${r.asset_id}`;
  return r.image_url || "";
};

export const ReferenceDrawer = ({ ref_, recceId, shareToken, onShareTokenMinted, onClose, onStar, onDelete }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const handler = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  if (!ref_) return null;

  return (
    <div
      data-testid="reference-drawer"
      className="fixed inset-0 z-50 flex items-stretch justify-end"
    >
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
        data-testid="drawer-backdrop"
      />
      <div className="relative w-full md:w-[760px] bg-bg border-l border-ink overflow-y-auto fade-rise">
        <div className="flex items-center justify-between px-6 py-3 border-b border-ink bg-ink text-bg">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em]">
            reference · {ref_.kind}
          </span>
          <button
            onClick={onClose}
            className="p-1 hover:text-amber"
            data-testid="drawer-close"
            aria-label="close drawer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid md:grid-cols-2">
          {/* Media side */}
          <div className="border-b md:border-b-0 md:border-r border-line p-6 bg-surface">
            {(["image", "imagined", "sketch"].includes(ref_.kind) ||
              (ref_.kind === "memory" && ref_.image_url)) ? (
              <img
                src={drawerImageSrc(ref_)}
                alt={ref_.title}
                className="w-full block border border-line"
                onError={(e) => (e.currentTarget.style.opacity = "0.2")}
              />
            ) : (
              <div className="font-serif text-3xl leading-tight">{ref_.title}</div>
            )}
            {ref_.kind === "memory" && (
              <div className="mt-4">
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber">
                  from memory · {ref_.category || "work"}
                </p>
                <p className="font-serif text-2xl leading-tight mt-2">{ref_.title}</p>
                {ref_.creator && (
                  <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-dim mt-1">
                    {ref_.creator}
                  </p>
                )}
              </div>
            )}
            {ref_.kind === "image" && ref_.title && (
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-dim mt-4">
                {ref_.title}
              </p>
            )}
          </div>

          {/* Meta side */}
          <div className="p-6 space-y-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">
                source
              </div>
              <a
                href={ref_.url}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center gap-2 font-mono text-xs underline underline-offset-4 break-all"
                data-testid="drawer-source-link"
              >
                {ref_.source || ref_.url || "—"}
                <ArrowUpRight size={12} />
              </a>
            </div>

            {ref_.snippet && (
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">
                  excerpt
                </div>
                <p className="mt-2 text-sm leading-relaxed text-ink">{ref_.snippet}</p>
              </div>
            )}

            {ref_.why_relevant && (
              <div className="border-t border-line pt-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim mb-2">
                  recce note
                </div>
                <p className="font-serif italic text-xl leading-snug">
                  “{ref_.why_relevant}”
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 pt-4 border-t border-line">
              <button
                onClick={() => onStar(ref_)}
                data-testid="drawer-star-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-ink font-mono text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-bg transition-colors"
              >
                <Star size={14} weight={ref_.starred ? "fill" : "regular"} />
                {ref_.starred ? "starred" : "star"}
              </button>
              <button
                onClick={async () => {
                  try {
                    let tok = shareToken;
                    if (!tok && recceId) {
                      const resp = await enableShare(recceId);
                      tok = resp.share_token;
                      onShareTokenMinted?.(tok);
                    }
                    const url = `${window.location.origin}/share/${tok}#ref=${ref_.id}`;
                    try {
                      await navigator.clipboard.writeText(url);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2200);
                      toast("Reference link copied", {
                        description: "Share it with another creative — opens straight to this image.",
                      });
                    } catch {
                      toast("Reference link ready", { description: url });
                    }
                  } catch (e) {
                    toast.error("Couldn't create a share link", {
                      description: e?.response?.data?.detail || "Try again in a moment.",
                    });
                  }
                }}
                data-testid="drawer-share-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-line text-ink-dim font-mono text-xs uppercase tracking-[0.18em] hover:text-ink hover:border-ink"
              >
                {copied ? <Check size={14} /> : <Share size={14} />}
                {copied ? "copied" : "share"}
              </button>
              <button
                onClick={() => {
                  onDelete(ref_);
                  onClose();
                }}
                data-testid="drawer-delete-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-line text-ink-dim font-mono text-xs uppercase tracking-[0.18em] hover:text-ink hover:border-ink"
              >
                <Trash size={14} />
                discard
              </button>
              {ref_.url && (
                <a
                  href={ref_.url}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-auto inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] underline underline-offset-4"
                  data-testid="drawer-open-source"
                >
                  open source <ArrowUpRight size={12} />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
